import React from 'react';
import CodeEditor from '@uiw/react-textarea-code-editor';
import '@uiw/react-textarea-code-editor/dist.css';
import { Box } from '@mui/material';
import { PG } from './theme';

const LANG_ALIASES: Record<string, string> = {
  html: 'html',
  htm: 'html',
  xml: 'markup',
  svg: 'markup',
  css: 'css',
  scss: 'css',
  less: 'css',
  js: 'js',
  javascript: 'js',
  mjs: 'js',
  cjs: 'js',
  jsx: 'jsx',
  ts: 'ts',
  typescript: 'ts',
  tsx: 'tsx',
  json: 'json',
  md: 'markdown',
  markdown: 'markdown',
  py: 'python',
  python: 'python',
  go: 'go',
  java: 'java',
  kt: 'kotlin',
  kotlin: 'kotlin',
  swift: 'swift',
  dart: 'dart',
  vue: 'html',
  svelte: 'html',
  cs: 'csharp',
  csharp: 'csharp',
  yaml: 'yaml',
  yml: 'yaml',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  shell: 'bash',
  sql: 'sql',
  dockerfile: 'docker',
  graphql: 'graphql',
  diff: 'diff',
};

const LARGE_FILE_CHARS = 160_000;

export function editorLanguage(path?: string, language?: string): string {
  const named = (language || '').toLowerCase().replace(/^text$/, '');
  const ext = (path || '').split(/[/\\]/).pop()?.split('.').pop()?.toLowerCase() || '';
  const raw = named || ext;
  return LANG_ALIASES[raw] || '';
}

type Props = {
  path?: string;
  language?: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  onSave?: () => void;
};

export const CodeFileEditor: React.FC<Props> = ({ path, language, value, onChange, onBlur, onSave }) => {
  const lang = value.length > LARGE_FILE_CHARS ? '' : editorLanguage(path, language);
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        border: '1px solid',
        borderColor: PG.border,
        borderRadius: 1,
        bgcolor: '#0a0f1a',
        '& .w-tc-editor': {
          minHeight: '100%',
          fontSize: 12,
          lineHeight: 1.5,
          backgroundColor: '#0a0f1a !important',
          color: '#e8eefc',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        },
        '& .w-tc-editor-text, & .w-tc-editor-preview': {
          minHeight: '100%',
        },
      }}
    >
      <CodeEditor
        value={value}
        language={lang}
        data-color-mode="dark"
        padding={12}
        indentWidth={2}
        placeholder="Empty file"
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
            e.preventDefault();
            onSave?.();
          }
        }}
        style={{
          minHeight: '100%',
          fontSize: 12,
          backgroundColor: '#0a0f1a',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        }}
      />
    </Box>
  );
};
